/**
 * S-82 (02.10.2026, właściciel: „jestem na ofercie marketplace, ale na karcie SellBase — chciałbym, żeby był URL w pasku do tego
 * drawera”; ciąg dalszy S-68 „URL aktualnego drawera zawsze powinien być”): adres szuflady oferty marketplace.
 *
 * Szuflada oferty (mpo.ts, nakładka nad każdą stroną) dopisuje do adresu strony POD SPODEM parametr
 * `oferta=<kanał>/<konto>/<numer oferty>`, np. `/catalog-v2/product/905?oferta=allegro/1/18832436201`. F5 albo wklejony link
 * otwiera tę samą szufladę nad tą samą stroną (kartą Katalogu, zamówieniem, listą ofert). Zamknięcie zdejmuje parametr.
 * Bez nowego wpisu w historii — „Wstecz” działa jak dotąd. Pakowanie (`packing-mode`) nie rusza adresu (jak S-68: F5 nie może
 * gubić sesji pakowania). Części wartości są kodowane osobno, więc numer oferty z „/” albo „&” nie rozjedzie adresu.
 */
export const PARAM_OFERTY = 'oferta';

export interface AdresOferty { marketplace: string; accountId: string; offerId: string }

let biezacy: AdresOferty | null = null;  // szuflada otwarta teraz (źródło prawdy dla stron, które same przepisują adres)
let otwieranyZAdresu = '';

export function wartoscAdresuOferty(a: AdresOferty): string {
  return [a.marketplace, a.accountId, a.offerId].map(c => encodeURIComponent(String(c ?? ''))).join('/');
}

/** S-199 (15.10.2026): stały, czysty link do oferty do wysłania (`https://sellbase.pl/oferta/prestashop/1/1:10416:0`) — bez
 *  strony pod szufladą, filtrów i wyszukiwania. Części, które przechodzą walidację odczytu, idą bez kodowania (dwukropek w numerze
 *  oferty PrestaShop jest bezpieczny w ścieżce); inne znaki — encodeURIComponent. */
export function linkDoOferty(a: AdresOferty, origin: string = (typeof window !== 'undefined' ? window.location.origin : '')): string {
  const czesc = (c: unknown): string => {
    const s = String(c ?? '');
    return /^[0-9A-Za-z_.:-]+$/.test(s) && !/^\.+$/.test(s) ? s : encodeURIComponent(s);
  };
  return `${origin}/oferta/${czesc(String(a.marketplace || '').toLowerCase())}/${czesc(a.accountId)}/${czesc(a.offerId)}`;
}

/** Odczyt z SUROWEGO `search` (URLSearchParams.get odkodowałby „%2F” i rozjechał podział na części). */
export function czytajAdresOferty(search: string = window.location.search): AdresOferty | null {
  const m = new RegExp(`[?&]${PARAM_OFERTY}=([^&#]*)`).exec(search || '');
  if (!m) return null;
  const czesci = m[1].split('/');
  if (czesci.length !== 3) return null;
  let wartosci: string[];
  try { wartosci = czesci.map(c => decodeURIComponent(c.replace(/\+/g, ' ')).trim()); } catch { return null; }
  const [marketplace, accountId, offerId] = wartosci;
  // numer z linku trafia do ścieżek API szuflady — tylko bezpieczne znaki (bez „/”, nie same kropki), żeby link nie wskazał innej trasy
  if (!/^[a-z][a-z0-9_]{1,31}$/i.test(marketplace) || !/^[0-9A-Za-z][0-9A-Za-z_.:-]{0,119}$/.test(offerId)) return null;
  if (accountId && !/^[0-9A-Za-z_.:-]{1,64}$/.test(accountId)) return null;
  return { marketplace: marketplace.toLowerCase(), accountId, offerId };
}

/** `url` = ścieżka + zapytanie + kotwica; podmienia (albo zdejmuje, gdy `a` puste) parametr oferty, resztę zostawia bez zmian. */
export function zAdresemOferty(url: string, a: AdresOferty | null): string {
  const hashAt = url.indexOf('#');
  const hash = hashAt >= 0 ? url.slice(hashAt) : '';
  const bezHash = hashAt >= 0 ? url.slice(0, hashAt) : url;
  const qAt = bezHash.indexOf('?');
  const sciezka = qAt >= 0 ? bezHash.slice(0, qAt) : bezHash;
  const pary = (qAt >= 0 ? bezHash.slice(qAt + 1) : '').split('&').filter(p => p && p.split('=')[0] !== PARAM_OFERTY);
  if (a) pary.push(`${PARAM_OFERTY}=${wartoscAdresuOferty(a)}`);
  return sciezka + (pary.length ? `?${pary.join('&')}` : '') + hash;
}

function wPakowaniu(): boolean { return document.body.classList.contains('packing-mode'); }

/** Szuflada oferty: otwarcie (`a`) / zamknięcie (`null`). Podmienia bieżący wpis historii. */
export function ustawAdresOferty(a: AdresOferty | null): void {
  biezacy = a && a.offerId ? { marketplace: String(a.marketplace || ''), accountId: String(a.accountId ?? ''), offerId: String(a.offerId) } : null;
  if (!biezacy) otwieranyZAdresu = '';
  if (wPakowaniu()) return;
  const teraz = window.location.pathname + window.location.search + window.location.hash;
  const nowy = zAdresemOferty(teraz, biezacy);
  if (nowy !== teraz) window.history.replaceState(window.history.state, '', nowy);
}

/** Dla stron, które same budują adres (Katalog V2): gdy szuflada oferty jest otwarta, jej parametr zostaje w nowym adresie. */
export function zachowajAdresOferty(url: string): string {
  const ov = document.getElementById('mpo-link-overlay');
  return biezacy && ov && ov.style.display !== 'none' && !wPakowaniu() ? zAdresemOferty(url, biezacy) : url;
}

// Link z ?oferta otwarty bez sesji: router zapamiętuje na czas logowania samą ścieżkę — ofertę trzymamy obok (jednorazowo, 30 min).
const KLUCZ_PO_LOGOWANIU = 'sellbase:oferta_po_logowaniu';
function zapiszNaLogowanie(sciezka: string, a: AdresOferty): void {
  try { sessionStorage.setItem(KLUCZ_PO_LOGOWANIU, JSON.stringify({ sciezka, oferta: wartoscAdresuOferty(a), kiedy: Date.now() })); } catch { /* bez pamięci sesji: link otworzy samą stronę */ }
}
function zPoLogowaniu(sciezka: string): AdresOferty | null {
  let z: any = null;
  try { z = JSON.parse(sessionStorage.getItem(KLUCZ_PO_LOGOWANIU) || 'null'); } catch { z = null; }
  if (!z || sciezka === '/login') return null;  // ekran logowania po drodze — wpis czeka na stronę docelową
  try { sessionStorage.removeItem(KLUCZ_PO_LOGOWANIU); } catch { /* noop */ }
  if (z.sciezka !== sciezka || !(Date.now() - Number(z.kiedy) < 30 * 60 * 1000)) return null;
  return czytajAdresOferty(`?${PARAM_OFERTY}=${String(z.oferta || '')}`);
}

/** Wejście na adres z `?oferta=…` (F5, wklejony link, „Wstecz” na taki adres) — otwórz szufladę nad stroną. */
function otworzZAdresu(): void {
  const zAdresu = czytajAdresOferty();
  const sciezka = window.location.pathname;
  if (!zAdresu) otwieranyZAdresu = '';
  // po routerze: strona, nad którą otwieramy, już się buduje, a stan logowania jest znany
  window.setTimeout(() => {
    const st = (window as any).__sbState || {};
    if (!st.loggedIn) { if (zAdresu) zapiszNaLogowanie(sciezka, zAdresu); otwieranyZAdresu = ''; return; }
    const a = zAdresu || zPoLogowaniu(window.location.pathname);
    if (!a) return;
    const klucz = wartoscAdresuOferty(a);
    if (klucz === otwieranyZAdresu || (biezacy && wartoscAdresuOferty(biezacy) === klucz)) return;
    otwieranyZAdresu = klucz;
    void import('./mpo.ts').then(mpo => mpo._openMpoLinkDrawer(a.marketplace, a.offerId, a.accountId))
      .catch(e => { otwieranyZAdresu = ''; console.warn('[S-82] szuflada oferty z adresu:', e); });
  }, 0);
}

if (typeof window !== 'undefined') {
  window.addEventListener('sb:route-change', otworzZAdresu);
  window.addEventListener('popstate', otworzZAdresu);
}
